import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { DateTime } from "luxon";
import { useEffect, useState } from "react";

import { DayOfWeekSelector } from "../../molecules/DayOfWeekSelector/DayOfWeekSelector";
import {
  createDefaultCustomRecurrence,
  type CustomRecurrenceConfig,
  type RecurrenceEndType,
  type RecurrenceFrequency,
} from "./AddEventTimesModal.recurrence";
import { errorOutlinedButtonSx, successDialogTitleSx, successGradientButtonSx } from "./AddEventTimesModal.theme";

interface CustomRecurrenceDialogProps {
  initialConfig: CustomRecurrenceConfig;
  onCancel: () => void;
  onDone: (config: CustomRecurrenceConfig) => void;
  open: boolean;
  startDate: DateTime | null;
}

const cloneConfig = (config: CustomRecurrenceConfig): CustomRecurrenceConfig => ({
  ...config,
  daysOfWeek: new Set(config.daysOfWeek),
  endDate: config.endDate,
});

export const CustomRecurrenceDialog = ({
  initialConfig,
  onCancel,
  onDone,
  open,
  startDate,
}: CustomRecurrenceDialogProps) => {
  const [config, setConfig] = useState<CustomRecurrenceConfig>(() => cloneConfig(initialConfig));

  useEffect(() => {
    if (open) {
      setConfig(cloneConfig(initialConfig));
    }
  }, [initialConfig, open]);

  useEffect(() => {
    if (open && startDate && config.daysOfWeek.size === 0 && config.frequency === "week") {
      const dayKey = startDate.weekday === 7 ? 0 : startDate.weekday;
      setConfig((current) => ({ ...current, daysOfWeek: new Set([dayKey]) }));
    }
  }, [config.daysOfWeek.size, config.frequency, open, startDate]);

  const handleDone = () => {
    const nextConfig =
      config.frequency === "week" && config.daysOfWeek.size === 0 && startDate
        ? { ...config, daysOfWeek: new Set([startDate.weekday === 7 ? 0 : startDate.weekday]) }
        : config;

    onDone(nextConfig);
  };

  const defaultConfig = startDate ? createDefaultCustomRecurrence(startDate) : null;

  return (
    <Dialog fullWidth maxWidth="xs" onClose={onCancel} open={open}>
      <DialogTitle sx={successDialogTitleSx}>Custom recurrence</DialogTitle>
      <DialogContent>
        <Stack spacing={3} sx={{ pt: 1 }}>
          <Box>
            <Stack alignItems="center" direction="row" spacing={1.5}>
              <Typography color="text.secondary" sx={{ flexShrink: 0 }} variant="body2">
                Repeat every
              </Typography>
              <TextField
                inputProps={{ min: 1 }}
                onChange={(event) => {
                  const interval = Math.max(1, Number(event.target.value) || 1);
                  setConfig((current) => ({ ...current, interval }));
                }}
                size="small"
                sx={{ width: 72 }}
                type="number"
                value={config.interval}
              />
              <FormControl size="small" sx={{ flex: 1, minWidth: 120 }}>
                <Select
                  onChange={(event) => {
                    const frequency = event.target.value as RecurrenceFrequency;
                    setConfig((current) => ({
                      ...current,
                      daysOfWeek:
                        frequency === "week"
                          ? current.daysOfWeek.size > 0
                            ? current.daysOfWeek
                            : defaultConfig
                              ? defaultConfig.daysOfWeek
                              : new Set<number>()
                          : new Set<number>(),
                      frequency,
                    }));
                  }}
                  value={config.frequency}
                >
                  <MenuItem value="day">day</MenuItem>
                  <MenuItem value="week">week</MenuItem>
                  <MenuItem value="month">month</MenuItem>
                  <MenuItem value="year">year</MenuItem>
                </Select>
              </FormControl>
            </Stack>
          </Box>

          {config.frequency === "week" ? (
            <DayOfWeekSelector
              instructionLabel="Repeat on"
              onDaysChange={(days) => setConfig((current) => ({ ...current, daysOfWeek: days }))}
              selectedDays={config.daysOfWeek}
            />
          ) : null}

          <Box>
            <Typography color="text.secondary" gutterBottom variant="body2">
              Ends
            </Typography>
            <RadioGroup
              onChange={(event) => {
                const endType = event.target.value as RecurrenceEndType;
                setConfig((current) => ({ ...current, endType }));
              }}
              sx={{ gap: 0.5 }}
              value={config.endType}
            >
              <FormControlLabel
                control={<Radio color="success" size="small" />}
                label="Never"
                sx={{ alignItems: "center", ml: 0, mr: 0 }}
                value="never"
              />
              <FormControlLabel
                control={<Radio color="success" size="small" />}
                label={
                  <Stack alignItems="center" direction="row" spacing={1} sx={{ ml: 0.5 }}>
                    <Typography variant="body2">On</Typography>
                    <TextField
                      disabled={config.endType !== "on"}
                      onChange={(event) => {
                        const dateString = event.target.value;
                        setConfig((current) => ({
                          ...current,
                          endDate: dateString ? DateTime.fromISO(dateString) : null,
                        }));
                      }}
                      onClick={(event) => event.stopPropagation()}
                      size="small"
                      slotProps={{
                        inputLabel: { shrink: true },
                      }}
                      sx={{ width: 160 }}
                      type="date"
                      value={config.endDate?.toFormat("yyyy-MM-dd") ?? ""}
                    />
                  </Stack>
                }
                sx={{ alignItems: "center", ml: 0, mr: 0 }}
                value="on"
              />
              <FormControlLabel
                control={<Radio color="success" size="small" />}
                label={
                  <Stack alignItems="center" direction="row" spacing={1} sx={{ ml: 0.5 }}>
                    <Typography variant="body2">After</Typography>
                    <TextField
                      disabled={config.endType !== "after"}
                      inputProps={{ min: 1 }}
                      onChange={(event) => {
                        const occurrenceCount = Math.max(1, Number(event.target.value) || 1);
                        setConfig((current) => ({ ...current, occurrenceCount }));
                      }}
                      onClick={(event) => event.stopPropagation()}
                      size="small"
                      sx={{ width: 72 }}
                      type="number"
                      value={config.occurrenceCount}
                    />
                    <Typography color="text.secondary" variant="body2">
                      occurrences
                    </Typography>
                  </Stack>
                }
                sx={{ alignItems: "center", ml: 0, mr: 0 }}
                value="after"
              />
            </RadioGroup>
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel} sx={errorOutlinedButtonSx} variant="outlined">
          Cancel
        </Button>
        <Button onClick={handleDone} sx={successGradientButtonSx} variant="contained">
          Done
        </Button>
      </DialogActions>
    </Dialog>
  );
};
